import ObjectId from "@shared/util/object-id-gen"
import { InputRule, mergeAttributes, Node, nodePasteRule } from "@tiptap/core"
import { closeHistory } from "@tiptap/pm/history"
import { NodeSelection, Plugin } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { find } from "linkifyjs"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asAttachmentId } from "../../analytics/types"
import {
  INLINE_RENDER_AS,
  isLinkNode,
  linkAttributes,
  type Link,
  type LinkDialogOrigin,
  type LinkRenderAs,
} from "./link-attrs"
import { LinkComponent } from "./link-component"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    linkInline: {
      /**
       * Switch the selected link token's form (docs/unified-links.md §6). Same-node
       * forms flip `renderAs` in place; inline ⇄ block conversions replace the node
       * with the other type, spreading the same attrs — lossless either way.
       */
      setLinkRenderAs: (renderAs: LinkRenderAs) => ReturnType
    }
  }
}

/**
 * Fresh Link attrs for a typed/pasted URL (docs/unified-links.md §6): provenance is
 * uniform (`source: "link"`, `type: "link"`), file fields null, and a new attachment
 * identity is minted.
 */
export function linkFromUrl(url: string, title: string): Link {
  return {
    attachmentId: ObjectId.create(),
    url,
    title,
    type: "link",
    mimetype: null,
    size: null,
    isWriteable: false,
    icon: null,
    source: "link",
    renderAs: "hyperlink",
    previewTitle: null,
    previewDescription: null,
    previewImageUrl: null,
    previewFaviconUrl: null,
  }
}

export interface LinkInlineOptions {
  openLinkDialog: ((origin: LinkDialogOrigin) => void) | null
}

/**
 * The inline form of the unified Link (docs/unified-links.md §4): a single uneditable
 * token, exactly like a @mention — select/delete/drag it as a unit; the cursor never
 * enters it. Renders as a hyperlink or chip per `renderAs`.
 */
export const ExtensionLinkInline = Node.create<LinkInlineOptions>({
  name: "linkInline",
  group: "inline",
  inline: true,
  atom: true, // uneditable single token; no cursor inside; delete removes whole; can't split
  selectable: true, // NodeSelection via the hover menu's Edit/Delete (a click opens the link)
  draggable: true,

  addOptions() {
    return {
      openLinkDialog: null,
    }
  },

  addAttributes: () => linkAttributes(INLINE_RENDER_AS),

  addKeyboardShortcuts() {
    const type = this.type
    return {
      // Enter right after a typed URL converts it like space does (§6). Input rules
      // don't fire on Enter, so this converts the word before the cursor itself, then
      // returns false so Enter still splits the paragraph as usual.
      Enter: ({ editor }) => {
        const { selection } = editor.state
        const $from = selection.$from
        if (!selection.empty || !$from.parent.isTextblock) return false
        const textBefore = $from.parent.textBetween(0, $from.parentOffset)
        const candidate = /(\S+)$/.exec(textBefore)?.[1]
        if (!candidate) return false
        const [link] = find(candidate, { defaultProtocol: "https" })
        if (!link?.isLink || link.value !== candidate) return false

        const start = $from.pos - candidate.length
        const attrs = linkFromUrl(link.href, candidate)
        editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_AUTOLINK, "keyboard", {
            attachmentId: asAttachmentId(attrs.attachmentId),
            url: attrs.url,
          })
          .command(({ tr }) => {
            closeHistory(tr).replaceWith(start, $from.pos, type.create(attrs))
            return true
          })
          .run()
        return false
      },

      // Mod-k opens the link dialog: over selected text it adds a token; over a
      // selected token (any form) it edits it.
      "Mod-k": ({ editor }) => {
        const { selection } = editor.state
        const onToken = selection instanceof NodeSelection && isLinkNode(selection.node)
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to)
        if (!onToken && selectedText === "") return false
        this.options.openLinkDialog?.("keyboard")
        return true
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as Link
    return [
      "a",
      mergeAttributes(HTMLAttributes, { "data-content-type": "linkInline", href: attrs.url }),
      attrs.title ?? attrs.url,
    ]
  },

  parseHTML() {
    return [
      // The editor's own serialized tokens round-trip with all attrs (data-*).
      { tag: 'a[data-content-type="linkInline"]', priority: 60 },
      // Any other <a href> — pasted rich HTML, or migrated legacy card HTML — becomes a
      // hyperlink token titled with the anchor's text (§6). Outranks the legacy link
      // mark's own a[href] rule; skips anchors serialized by other unified nodes.
      {
        tag: "a[href]",
        priority: 52,
        getAttrs: (element) => {
          if (element.hasAttribute("data-content-type")) return false
          const href = element.getAttribute("href")
          if (!href) return false
          return linkFromUrl(href, element.textContent || href)
        },
      },
    ]
  },

  addInputRules() {
    const type = this.type
    return [
      // Typing a URL + space replaces the matched run with a hyperlink token (§6).
      // The broad word-plus-space match fires on every word; linkify decides whether
      // it's a URL (scheme-less domains like nytimes.com included) — returning null
      // cancels the rule, so the space inserts normally.
      new InputRule({
        find: /(?:^|\s)(\S+) $/,
        handler({ state, range, match, chain }) {
          const candidate = match[1]
          const [link] = find(candidate, { defaultProtocol: "https" })
          if (!link?.isLink || link.value !== candidate) return null
          const start = range.to - candidate.length
          const attrs = linkFromUrl(link.href, candidate)
          // Its own history event, so ⌘Z reverts just the conversion — back to the
          // typed URL text — instead of the whole typing group (§6's escape hatch).
          closeHistory(state.tr).replaceWith(start, range.to, [
            type.create(attrs),
            state.schema.text(" "),
          ])
          // The rule's `chain` is bound to the very transaction above, so this labels it.
          chain()
            .label(OPERATION_CONTRACTS.OP_AUTOLINK, "markdown", {
              attachmentId: asAttachmentId(attrs.attachmentId),
              url: attrs.url,
            })
            .run()
        },
      }),
    ]
  },

  // Defined once here for both node types (the command registry is editor-global).
  addCommands() {
    return {
      setLinkRenderAs:
        (renderAs: LinkRenderAs) =>
        ({ state, tr, dispatch }) => {
          const { selection } = state
          if (!(selection instanceof NodeSelection) || !isLinkNode(selection.node)) return false
          const node = selection.node
          const attrs = { ...(node.attrs as Link), renderAs }
          const targetTypeName = (INLINE_RENDER_AS as readonly string[]).includes(renderAs)
            ? "linkInline"
            : "linkBlock"

          if (node.type.name === targetTypeName) {
            if (dispatch && attrs.renderAs !== node.attrs.renderAs) {
              tr.setNodeMarkup(selection.from, undefined, attrs)
            }
            return true
          }

          if (dispatch) {
            const token = state.schema.nodes[targetTypeName].create(attrs)
            let tokenPos: number
            if (targetTypeName === "linkBlock") {
              const $from = state.doc.resolve(selection.from)
              if ($from.parent.childCount === 1) {
                // The token is its paragraph's only child: the paragraph becomes the block.
                tokenPos = $from.before()
                tr.replaceWith($from.before(), $from.after(), token)
              } else {
                // Pull the token out of its paragraph, onto its own row right after it.
                const insertPos = $from.after()
                tr.delete(selection.from, selection.to)
                tokenPos = tr.mapping.map(insertPos)
                tr.insert(tokenPos, token)
              }
            } else {
              // block → inline: wrap in a paragraph in place.
              tokenPos = selection.from + 1
              tr.replaceWith(
                selection.from,
                selection.to,
                state.schema.nodes.paragraph.create(null, token),
              )
            }
            // Keep the token selected so the menu/dialog keep operating on it.
            tr.setSelection(NodeSelection.create(tr.doc, tokenPos))
          }
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    const type = this.type
    return [
      new Plugin({
        props: {
          // Pasting a URL over selected text keeps the text: the selection becomes a
          // hyperlink token *titled with the selected text* (§6) — the token-world
          // version of the old mark's linkOnPaste. Bare-cursor URL pastes fall through
          // to the paste rule below.
          handlePaste(view, event) {
            const { selection, doc, tr } = view.state
            if (selection.empty || !view.editable) return false
            const text = event.clipboardData?.getData("text/plain").trim()
            if (!text) return false
            const [link] = find(text, { defaultProtocol: "https" })
            if (!link?.isLink || link.value !== text) return false

            // Pasting over a selected token re-links it: same attachment identity and
            // label, new destination — like editing its URL in the dialog.
            const selectedToken =
              selection instanceof NodeSelection && isLinkNode(selection.node)
                ? (selection.node.attrs as Link)
                : null
            const attrs = selectedToken
              ? {
                  ...linkFromUrl(link.href, selectedToken.title),
                  attachmentId: selectedToken.attachmentId,
                }
              : linkFromUrl(link.href, doc.textBetween(selection.from, selection.to, " "))
            view.dispatch(closeHistory(tr).replaceSelectionWith(type.create(attrs)))
            return true
          },
        },
      }),
    ]
  },

  addPasteRules() {
    return [
      // A URL on the clipboard (bare, or inside pasted text) inserts a hyperlink
      // token titled with the URL as written (§6).
      nodePasteRule({
        find: (text) =>
          find(text, { defaultProtocol: "https" })
            .filter((link) => link.isLink)
            .map((link) => ({ index: link.start, text: link.value, data: link })),
        type: this.type,
        getAttributes: (match) => {
          const link = match.data as { href: string; value: string }
          return linkFromUrl(link.href, link.value)
        },
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkComponent)
  },
})
